import type { HTMLAttributes, ReactNode } from 'react'

import { cn } from '../cn'

export type PanelProps = HTMLAttributes<HTMLDivElement>

/** The prototype's `.panel`: white, 1 px line, 10 px radius, soft shadow. */
export function Panel({ className, ...rest }: PanelProps) {
  return (
    <div
      className={cn('rounded-md border border-line bg-panel shadow-panel', className)}
      {...rest}
    />
  )
}

export interface PanelHeaderProps {
  readonly title: ReactNode
  readonly description?: ReactNode
  readonly actions?: ReactNode
  readonly className?: string
}

export function PanelHeader({ title, description, actions, className }: PanelHeaderProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-b border-line px-[18px] py-4',
        className,
      )}
    >
      <div className="min-w-0">
        <h2 className="text-[15px] font-semibold">{title}</h2>
        {description !== undefined && (
          <p className="mt-0.5 text-[12.5px] text-muted">{description}</p>
        )}
      </div>
      {actions !== undefined && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  )
}

export function PanelBody({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-[18px] py-4', className)} {...rest} />
}

export interface StatCardProps {
  readonly label: ReactNode
  readonly value: ReactNode
  /** Small line under the value, e.g. "+12% vs last week". */
  readonly delta?: ReactNode
  readonly tone?: 'neutral' | 'recovered' | 'atrisk' | 'lost' | 'info'
  readonly icon?: ReactNode
  readonly className?: string
}

const STAT_TONE = {
  neutral: 'text-muted',
  recovered: 'text-recovered',
  atrisk: 'text-atrisk',
  lost: 'text-lost',
  info: 'text-info',
} as const

/** The prototype's `.stat-card`: label, Fraunces 28 px value, optional delta and icon. */
export function StatCard({
  label,
  value,
  delta,
  tone = 'neutral',
  icon,
  className,
}: StatCardProps) {
  return (
    <Panel className={cn('flex flex-col gap-2 px-[18px] py-4', className)}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-bold tracking-[0.05em] text-muted-2 uppercase">
          {label}
        </span>
        {icon !== undefined && (
          <span aria-hidden className={cn('[&>svg]:size-4', STAT_TONE[tone])}>
            {icon}
          </span>
        )}
      </div>
      <div className="font-display text-[28px] leading-none font-semibold text-ink">{value}</div>
      {delta !== undefined && (
        <div className={cn('text-[12px] font-medium', STAT_TONE[tone])}>{delta}</div>
      )}
    </Panel>
  )
}

export interface KeyValueGridProps {
  readonly items: readonly { readonly label: ReactNode; readonly value: ReactNode }[]
  readonly columns?: 1 | 2 | 3
  readonly className?: string
}

export function KeyValueGrid({ items, columns = 2, className }: KeyValueGridProps) {
  return (
    <dl
      className={cn(
        'grid gap-x-6 gap-y-3',
        columns === 1
          ? 'grid-cols-1'
          : columns === 2
            ? 'grid-cols-1 sm:grid-cols-2'
            : 'grid-cols-1 sm:grid-cols-3',
        className,
      )}
    >
      {items.map((item, index) => (
        <div key={index} className="min-w-0">
          <dt className="text-[11px] font-bold tracking-[0.05em] text-muted-2 uppercase">
            {item.label}
          </dt>
          <dd className="mt-0.5 text-[13.5px] break-words text-ink">{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}
